
import { computed, defineComponent, onMounted, Ref, ref } from 'vue';
import { useBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import useVideoPlayer from '@/b2c/shared/video-player.composable';
import { onBeforeUnmount } from '@vue/runtime-core';

export default defineComponent({
    name: 'VideoPlayer',
    props: {
        url: {
            required: true,
            type: String,
        },
        poster: {
            required: true,
            type: String,
        },
    },
    emits: ['video-play', 'video-ended', 'video-progress'],
    setup(props, { emit }) {
        const videoRef: Ref<HTMLVideoElement | null> = ref(null);
        const progressBarRef: Ref<HTMLElement | null> = ref(null);
        const {
            enterFullScreen,
            fadeOutTools,
            fadeInTools,
            boostVolume,
            lowerVolume,
            rewindFiveSeconds,
            skipFiveSeconds,
            skipToClickedTime,
            togglePlaying,
            toggleSound,
            onTimeProgress,
            durationRaw,
            currentTimeRaw,
            currentTime,
            progressLabelStyling,
            progressBarStyling,
            showTools,
            percentPlayed,
            percentPlayedRaw,
            paused,
            muted,
        } = useVideoPlayer(videoRef as Ref<HTMLVideoElement>, progressBarRef as Ref<HTMLVideoElement>);
        let percentsAlreadyTracked = 0;
        let percentsInterval = 0;
        const labels = useBlockLabels();
        const playButtonLabel = computed(() => paused.value ? labels.playVideo : labels.pauseVideo);
        const soundButtonLabel = computed(() => muted.value ? labels.unmuteVideo : labels.muteVideo);
        const fullscreenButtonLabel = labels.enterFullscreen;

        onTimeProgress(() => {
            emitProgressEvent();
        });

        const emitProgressEvent = () => {
            if (percentPlayedRaw.value > percentsAlreadyTracked) {
                if (percentsInterval >= 10) {
                    emit('video-progress', percentPlayed.value);
                    percentsInterval = 0;
                }
                percentsInterval++;
                percentsAlreadyTracked = percentPlayedRaw.value;
            }
        };

        const emitVideoPlayEvent = () => {
            emit('video-play', true);
        };

        const emitVideoEndedEvent = () => {
            emit('video-ended', true);
        };

        onMounted(() => {
            if (videoRef.value) {
                videoRef.value.addEventListener('play', emitVideoPlayEvent);
                videoRef.value.addEventListener('ended', emitVideoEndedEvent);
            }
        });

        onBeforeUnmount(() => {
            if (videoRef.value) {
                videoRef.value.removeEventListener('play', emitVideoPlayEvent);
                videoRef.value.removeEventListener('ended', emitVideoEndedEvent);
            }
        });

        return {
            currentTime,
            currentTimeRaw,
            durationRaw,
            fullscreenButtonLabel,
            muted,
            paused,
            progressLabelStyling,
            playButtonLabel,
            progressBarRef,
            progressBarStyling,
            percentPlayed,
            videoRef,
            showTools,
            soundButtonLabel,
            boostVolume,
            lowerVolume,
            enterFullScreen,
            fadeInTools,
            fadeOutTools,
            rewindFiveSeconds,
            skipFiveSeconds,
            skipToClickedTime,
            togglePlaying,
            toggleSound,
        };
    },
});
